import { knownAiTool } from '@vd/types';

/**
 * Agent activity's rows (TOADO-951): within one ticket's block, one row per
 * agent, named once and short, marked with its tool when the name says which,
 * and a summary of what it did; the steps themselves fold under the row.
 */
export type AgentStep = { agent: string; did: string; at: string };

export type AgentRow<S extends AgentStep = AgentStep> = {
  agent: string;
  /** The name as the card shows it: cut to about 24 characters. */
  short: string;
  /** The tool the name says it is ("Claude", "Cursor"), or null for a typed label. */
  tool: string | null;
  /** "moved 2, commented 1", in the order the agent first did each, without the verb's last word ("commented on"). */
  summary: string;
  latest: string;
  steps: S[];
};

/** How long a name may be before it is cut, with the whole of it on hover. */
export const AGENT_NAME_CHARS = 24;

export function shortAgentName(name: string): string {
  const chars = [...name];
  return chars.length <= AGENT_NAME_CHARS ? name : `${chars.slice(0, AGENT_NAME_CHARS - 1).join('').trimEnd()}…`;
}

/** One ticket's steps, newest first as they come, grouped by agent in the order each first appears. */
export function agentRows<S extends AgentStep>(steps: S[]): AgentRow<S>[] {
  const rows = new Map<string, S[]>();
  for (const step of steps) rows.set(step.agent, [...(rows.get(step.agent) ?? []), step]);
  return [...rows].map(([agent, mine]) => {
    const did = new Map<string, number>();
    for (const step of mine) did.set(step.did, (did.get(step.did) ?? 0) + 1);
    return {
      agent,
      short: shortAgentName(agent),
      tool: knownAiTool(agent),
      summary: [...did].map(([what, count]) => `${what.replace(/ (on|for|to)$/, '')} ${count}`).join(', '),
      latest: mine.reduce((newest, step) => (step.at > newest ? step.at : newest), mine[0]!.at),
      steps: mine,
    };
  });
}
